/**
 * Word Factory Section
 * Handles the word generation form and API interaction
 */

class WordFactory {
  constructor(root) {
    this.root = root;
    this.sectionId = root.id.replace('word-factory-', '');

    // Get API config from data attributes
    this.endpoint = root.dataset.apiEndpoint;
    this.authToken = root.dataset.apiToken;

    // Cache DOM elements
    this.elements = {
      who: root.querySelector(`#wf-who-${this.sectionId}`),
      what: root.querySelector(`#wf-what-${this.sectionId}`),
      when: root.querySelector(`#wf-when-${this.sectionId}`),
      statusWrap: root.querySelector('.wf-status'),
      statusText: root.querySelector('.wf-status-text'),
      resultWrap: root.querySelector('.wf-result'),
      word: root.querySelector('[data-word]'),
      pronunciation: root.querySelector('[data-pronunciation]'),
      definition: root.querySelector('[data-definition]'),
      btnGenerate: root.querySelector('[data-action="generate"]'),
      btnReset: root.querySelector('[data-action="reset"]'),
      productList: this.findProductListSection()
    };

    console.log('🏭 Word Factory initialized');
    console.log('  Section ID:', this.sectionId);
    console.log('  Root element:', root);
    console.log('  Generate button:', this.elements.btnGenerate);
    console.log('  API endpoint:', this.endpoint);

    if (!this.elements.btnGenerate) {
      console.error('❌ Generate button not found!');
      return;
    }

    // State
    this.lastWord = '';
    this.lastDefinition = '';

    // Bind events
    this.elements.btnGenerate.addEventListener('click', () => this.generate());
    this.elements.btnReset.addEventListener('click', () => this.reset());

    // Hide product list initially
    this.hideProductList();
  }

  findProductListSection() {
    // Find the next product-list section after this word-factory section
    const wordFactorySection = this.root.closest('.shopify-section');
    if (!wordFactorySection) return null;

    let nextSection = wordFactorySection.nextElementSibling;
    while (nextSection) {
      const productList = nextSection.querySelector('[data-testid="product-list"]');
      if (productList) return productList;
      nextSection = nextSection.nextElementSibling;
    }
    return null;
  }

  hideProductList() {
    if (this.elements.productList) {
      this.elements.productList.style.display = 'none';
    }
  }

  showProductList() {
    if (this.elements.productList) {
      this.elements.productList.style.display = '';
    }
  }

  setStatus(text, loading = false) {
    if (this.elements.statusText) {
      this.elements.statusText.textContent = text || '';
    }

    if (loading) {
      this.root.classList.add('word-factory--loading');
    } else {
      this.root.classList.remove('word-factory--loading');
    }
  }

  getPayload() {
    return {
      who: (this.elements.who.value || '').trim(),
      what: (this.elements.what.value || '').trim(),
      when: (this.elements.when.value || '').trim()
    };
  }

  async generate() {
    console.log('🔨 Generate button clicked!');

    const data = this.getPayload();
    console.log('📝 Form data:', data);

    if (!data.who && !data.what && !data.when) {
      console.log('⚠️ No fields filled');
      this.setStatus('Please fill at least one field.', false);
      return;
    }

    this.elements.btnGenerate.disabled = true;
    this.setStatus('Forging your word…', true);
    console.log('🌐 Calling API...');

    try {
      console.log('🚀 Fetching from:', this.endpoint);

      const res = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.authToken}`
        },
        body: JSON.stringify(data)
      });

      console.log('📡 Response status:', res.status);

      if (!res.ok) {
        const errorText = await res.text().catch(() => '');
        console.error('❌ API error:', errorText);
        throw new Error(errorText || `Request failed: ${res.status}`);
      }

      const result = await res.json().catch(() => ({}));
      console.log('✅ API response:', result);

      const item = result?.output?.forged?.[0];

      const word = item?.word ? String(item.word) : '';
      const pronunciation = item?.pronunciation ? String(item.pronunciation) : '';
      const definition = item?.definition ? String(item.definition) : '';

      if (!word) {
        throw new Error('No word returned from API.');
      }

      this.lastWord = word;
      this.lastDefinition = definition || `A special word coined for ${data.who || 'this moment'}.`;

      // Update DOM
      this.elements.word.textContent = word;

      if (pronunciation) {
        this.elements.pronunciation.textContent = pronunciation;
        this.elements.pronunciation.hidden = false;
      } else {
        this.elements.pronunciation.hidden = true;
      }

      this.elements.definition.textContent = this.lastDefinition;

      // Show result with fade-in animation
      this.elements.resultWrap.hidden = false;
      this.elements.resultWrap.classList.remove('wf-result--show');
      // Force reflow to restart animation
      void this.elements.resultWrap.offsetWidth;
      this.elements.resultWrap.classList.add('wf-result--show');

      this.setStatus('', false);

      // Show the product list section
      this.showProductList();

      // Save to localStorage for Teeinblue autofill
      try {
        localStorage.setItem('nwf_word', this.lastWord);
        localStorage.setItem('nwf_definition', this.lastDefinition);
        localStorage.setItem('nwf_pronunciation', pronunciation || '');
        localStorage.setItem('nwf_meta', JSON.stringify(data));
        console.log('💾 Saved to localStorage');
      } catch (e) {
        console.warn('Failed to save to localStorage:', e);
      }

    } catch (err) {
      console.error('❌ Word Factory error:', err);
      this.setStatus('Error generating your word. Please try again.', false);
    } finally {
      this.elements.btnGenerate.disabled = false;
    }
  }

  reset() {
    console.log('🔄 Resetting Word Factory');

    this.elements.who.value = '';
    this.elements.what.value = '';
    this.elements.when.value = '';

    this.setStatus('', false);
    this.elements.resultWrap.hidden = true;
    this.elements.resultWrap.classList.remove('wf-result--show');

    this.lastWord = '';
    this.lastDefinition = '';

    // Hide the product list section
    this.hideProductList();

    try {
      localStorage.removeItem('nwf_word');
      localStorage.removeItem('nwf_definition');
      localStorage.removeItem('nwf_pronunciation');
      localStorage.removeItem('nwf_meta');
      console.log('🗑️ Cleared localStorage');
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }
  }
}

// Initialize all Word Factory sections on the page
document.addEventListener('DOMContentLoaded', () => {
  const wordFactories = document.querySelectorAll('[id^="word-factory-"]');
  console.log(`🏭 Found ${wordFactories.length} Word Factory section(s)`);

  wordFactories.forEach(root => {
    new WordFactory(root);
  });
});
